import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { messageLog } from 'utilities/helpers/logger/message-logger.js';
import { isNotNullOrUndefined } from 'utilities/isType.js';

type ITabsV2Props<T extends number | string> = {
  children: JSX.Element;
  initialTabKey?: T;
  activeTabCallback?: (key: T) => void;
  tabContainerClasses?: string;
} & ITabsV2Core<T>;

type ITabsV2Core<T> = {
  tabHeaderClassByKey?: (key: T) => string;
  tabHeaderClass?: string;
  headers: { content: string | JSX.Element; key: T }[];
};

function CreateTabHeader<T extends number | string>({
  headers,
  isActive,
  setActiveTabIndex,
  tabHeaderClass,
  tabHeaderClassByKey,
}: {
  isActive: boolean;
  setActiveTabIndex: (index: T) => void;
} & ITabsV2Core<T>) {
  const header = headers[0];
  return (
    <div
      key={header.key}
      className={classNames('Tab-item', { 'is-active': isActive }, tabHeaderClass ?? '', tabHeaderClassByKey?.(header.key))}
      onClick={() => setActiveTabIndex(header.key)}
    >
      {header.content}
    </div>
  );
}

export function TabsV2<T extends number | string>({
  initialTabKey,
  headers,
  children,
  activeTabCallback,
  tabContainerClasses,
  tabHeaderClass,
  tabHeaderClassByKey,
}: ITabsV2Props<T>) {
  const initKey = initialTabKey ?? headers[0]?.key;

  const [activeTabKey, _setActiveTabKey] = useState(initKey);

  useEffect(() => {
    _setActiveTabKey(initKey);
  }, [initKey]);

  const setActiveTabIndex = useCallback((key: T) => {
    _setActiveTabKey(key);
  }, []);

  useEffect(() => {
    if (isNotNullOrUndefined(activeTabKey)) {
      activeTabCallback?.(activeTabKey);
    }
  }, [activeTabKey, activeTabCallback]);

  if (headers.length <= 1) {
    messageLog.error(`headers only ${headers.length} long`);
    return children;
  }

  // The has-warning class is triggered based on if the the component passed into headers
  // has a hasWarning boolean property
  // For example:
  // return <TabHeader day={day} hasWarning={hasWarning} key={idx} />;
  return (
    <>
      <div data-comp="tabs-v2" className={classNames('Tab', tabContainerClasses)}>
        {headers.map((header, i) => (
          <CreateTabHeader
            headers={[header]}
            key={i}
            isActive={activeTabKey === header.key}
            setActiveTabIndex={setActiveTabIndex}
            tabHeaderClass={tabHeaderClass}
            tabHeaderClassByKey={tabHeaderClassByKey}
          />
        ))}
      </div>
      {children}
    </>
  );
}
